import { isNotFound, isRedirect } from '@tanstack/react-router';
import {
    createCsrfMiddleware,
    createMiddleware,
    createStart,
} from '@tanstack/react-start';

import { toSerializableAppError } from '@/lib/errors';
import { PRODUCTION_HSTS } from '@/lib/security-headers';

/**
 * Re-throw server-fn failures as richly detailed, serializable Errors so the
 * error page can show PostgreSQL / Drizzle fields after SSR or client fetch.
 */
const detailedErrorsMiddleware = createMiddleware({ type: 'function' }).server(
    async ({ next }) => {
        const result = await next();
        if (result && typeof result === 'object' && 'error' in result && result.error) {
            const error = result.error;
            if (isRedirect(error) || isNotFound(error)) {
                throw error;
            }
            throw toSerializableAppError(error);
        }
        return result;
    },
);

const securityHeadersMiddleware = createMiddleware().server(
    async ({ next }) => {
        const result = await next();
        if (process.env.NODE_ENV !== 'production') return result;
        if (!(result instanceof Response)) return result;

        const headers = new Headers(result.headers);
        if (!headers.has('Strict-Transport-Security')) {
            headers.set('Strict-Transport-Security', PRODUCTION_HSTS);
        }
        return new Response(result.body, {
            status: result.status,
            statusText: result.statusText,
            headers,
        });
    },
);

const csrfMiddleware = createCsrfMiddleware({
    filter: (ctx) => ctx.handlerType === 'serverFn',
});

export const startInstance = createStart(() => {
    return {
        requestMiddleware: [securityHeadersMiddleware, csrfMiddleware],
        functionMiddleware: [detailedErrorsMiddleware],
    };
});
